import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { supabase } from './data/supabaseClient';
import { useToast } from './hooks/ToastContext';
import { FaTimes, FaCalendarAlt, FaClock, FaFileAlt, FaRoute, FaMapMarkerAlt, FaExclamationTriangle, FaInfoCircle } from 'react-icons/fa';
import { UnifiedDatePicker, UnifiedTimePicker } from './common/UnifiedDateTimePicker';
import { CustomButton10Co, CustomButton10Ca, CustomButton2, RadioButton1 } from './Templates';
import Spinner from './common/Spinner';
import './PostRenewalForm.css';

/**
 * PostRenewalForm - Allows users to create a new post based on a completed one
 * Only date, time, and description can be edited
 */
const PostRenewalForm = ({ 
  isOpen,
  onClose,
  originalPost,
  postType, // 'ride' or 'package'
  onSuccess 
}) => {
  const { t } = useTranslation('common');
  const { addToast } = useToast();
  const navigate = useNavigate();
  
  // Form state
  const [travelDate, setTravelDate] = useState(null);
  const [travelTime, setTravelTime] = useState(null);
  const [description, setDescription] = useState('');
  const [travelType, setTravelType] = useState('One-time');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Advance booking limits state
  const [advanceBookingLimits, setAdvanceBookingLimits] = useState(null);
  const [dateValidationError, setDateValidationError] = useState(null);
  const [limitsLoading, setLimitsLoading] = useState(false);
  
  // Monthly limits state
  const [monthlyLimits, setMonthlyLimits] = useState(null);
  const [monthlyLimitsLoading, setMonthlyLimitsLoading] = useState(false);
  
  // Fetch limits when form opens
  useEffect(() => {
    const fetchLimits = async () => {
      if (!isOpen) return;
      
      try {
        setLimitsLoading(true);
        setMonthlyLimitsLoading(true);
        
        const { data: { user }, error: userError } = await supabase.auth.getUser();
        if (userError || !user) return;
        
        // Fetch advance booking limits
        const { data: advanceLimitData, error: advanceLimitError } = await supabase.rpc('get_user_advance_booking_limits', {
          p_user_id: user.id
        });
        
        if (advanceLimitError) {
          console.error('Error checking advance booking limits:', advanceLimitError);
        } else if (advanceLimitData) {
          console.log('Advance booking limits data:', advanceLimitData);
          setAdvanceBookingLimits(advanceLimitData);
        }
        
        // Fetch monthly limits based on post type
        const monthlyLimitRpc = postType === 'ride' ? 'check_monthly_post_limit' : 'check_monthly_package_limit';
        const { data: monthlyLimitData, error: monthlyLimitError } = await supabase.rpc(monthlyLimitRpc, {
          p_user_id: user.id
        });
        
        if (monthlyLimitError) {
          console.error('Error checking monthly limits:', monthlyLimitError);
        } else if (monthlyLimitData) {
          console.log('Monthly limits data:', monthlyLimitData);
          setMonthlyLimits(monthlyLimitData);
        }
        
      } catch (error) {
        console.error('Error fetching limits:', error);
      } finally {
        setLimitsLoading(false);
        setMonthlyLimitsLoading(false);
      }
    };
    
    fetchLimits();
  }, [isOpen, postType]);
  
  // Initialize form with original post data
  useEffect(() => {
    if (originalPost && isOpen) {
      // Set date to tomorrow by default
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      setTravelDate(tomorrow);
      
      // Try to parse original time
      let parsedTime = null;
      if (originalPost.travel_time) {
        try {
          const [hours, minutes] = originalPost.travel_time.split(':');
          const timeDate = new Date();
          timeDate.setHours(parseInt(hours) || 0, parseInt(minutes) || 0, 0, 0);
          parsedTime = timeDate;
          setTravelTime(timeDate);
        } catch (e) {
          console.error('Error parsing time:', e);
          setTravelTime(null);
        }
      }
      
      // Set description
      setDescription(originalPost.description || '');
      
      // Set travel type
      setTravelType(originalPost.travel_type || 'One-time');
      
      // Validate the initial date/time combination if both are available
      if (parsedTime && advanceBookingLimits) {
        setTimeout(() => {
          validateAdvanceBookingDate(tomorrow, parsedTime);
        }, 100); // Small delay to ensure state is updated
      }
    }
  }, [originalPost, isOpen, advanceBookingLimits]);
  
  // Function to validate advance booking date limits
  const validateAdvanceBookingDate = async (checkDate, checkTime) => {
    if (!checkDate || !checkTime || !advanceBookingLimits) return;
    
    // Format date and time for the API call
    const travelDate = checkDate.toISOString().split('T')[0]; // YYYY-MM-DD format
    const travelTime = checkTime.toTimeString().split(' ')[0]; // HH:MM:SS format
    
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) return;
      
      const { data: validationResult, error } = await supabase.rpc('check_advance_booking_limit', {
        p_user_id: user.id,
        p_travel_date: travelDate,
        p_travel_time: travelTime
      });
      
      if (error) {
        console.error('Error validating advance booking date:', error);
        return;
      }
      
      if (!validationResult.can_create) {
        setDateValidationError({
          message: validationResult.error,
          maxDays: validationResult.max_advance_days,
          userPlan: validationResult.user_plan,
          daysInAdvance: validationResult.days_in_advance
        });
      } else {
        setDateValidationError(null);
      }
    } catch (err) {
      console.error('Exception validating advance booking date:', err);
    }
  };
  
  // Handle date/time changes with validation
  const handleDateChange = (newDate) => {
    setTravelDate(newDate);
    if (newDate && travelTime) {
      validateAdvanceBookingDate(newDate, travelTime);
    }
  };
  
  const handleTimeChange = (newTime) => {
    setTravelTime(newTime);
    if (travelDate && newTime) {
      validateAdvanceBookingDate(travelDate, newTime);
    }
  };
  
  if (!isOpen || !originalPost) return null;
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate inputs
    if (!travelDate) {
      addToast({
        type: 'error',
        title: t('errors.validation_error'),
        description: t('errors.date_required', { defaultValue: 'Please select a travel date' })
      });
      return;
    }
    
    if (!travelTime) {
      addToast({
        type: 'error',
        title: t('errors.validation_error'),
        description: t('errors.time_required', { defaultValue: 'Please select a travel time' })
      });
      return;
    }
    
    // Check for advance booking date validation errors
    if (dateValidationError) {
      addToast({
        type: 'error',
        title: t('errors.validation_error'),
        description: dateValidationError.message
      });
      return;
    }
    
    // Check monthly limits
    if (monthlyLimits && !monthlyLimits.has_remaining) {
      addToast({
        type: 'error',
        title: t('errors.limit_reached'),
        description: monthlyLimits.max_allowed === 0 
          ? `${postType === 'ride' ? 'Ride' : 'Package'} posting requires at least a Basic subscription. Please upgrade your plan.`
          : `You've reached your monthly limit of ${monthlyLimits.max_allowed} ${postType === 'ride' ? 'rides' : 'packages'}. Upgrade your plan for more!`
      });
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Format date and time
      const formattedDate = travelDate.toISOString().split('T')[0];
      const formattedTime = `${String(travelTime.getHours()).padStart(2, '0')}:${String(travelTime.getMinutes()).padStart(2, '0')}:00`;
      
      // Get current user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;
      
      // Call the appropriate RPC function
      const { data, error } = postType === 'ride'
        ? await supabase.rpc('create_renewal_post', {
            p_original_post_id: originalPost.id,
            p_user_id: user.id,
            p_travel_date: formattedDate,
            p_travel_time: formattedTime,
            p_description: description.trim(),
            p_travel_type: travelType
          })
        : await supabase.rpc('create_renewal_package', {
            p_original_package_id: originalPost.id,
            p_user_id: user.id,
            p_travel_date: formattedDate,
            p_travel_time: formattedTime,
            p_description: description.trim(),
            p_travel_type: travelType
          });
      
      if (error) throw error;
      
      if (!data.success) {
        if (data.error === 'monthly_limit_exceeded') {
          addToast({
            type: 'error',
            title: t('errors.limit_exceeded'),
            description: t('errors.monthly_limit_message', { 
              defaultValue: 'You have reached your monthly limit. Please upgrade your plan to post more.' 
            })
          });
        } else {
          throw new Error(data.message || 'Failed to create renewal');
        }
        return;
      }
      
      // Success
      addToast({
        type: 'success',
        title: t('success.post_created'),
        description: postType === 'ride'
          ? t('success.ride_renewed', { defaultValue: 'Your ride has been posted successfully!' })
          : t('success.package_renewed', { defaultValue: 'Your package has been posted successfully!' })
      });
      
      if (onSuccess) {
        onSuccess(data[postType === 'ride' ? 'post_id' : 'package_id']);
      }
      
      onClose();
      
    } catch (error) {
      console.error('Error creating renewal:', error);
      addToast({
        type: 'error',
        title: t('errors.renewal_failed'),
        description: error.message || t('errors.generic_error')
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Format address for display
  const formatAddress = (prefix) => {
    const street = originalPost[`address_${prefix}_street`];
    const city = originalPost[`address_${prefix}_city`];
    const country = originalPost[`address_${prefix}_country`];
    
    const parts = [street, city, country].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : t('posts.not_specified');
  };
  
  return (
    <div className="PRF-overlay" onClick={onClose}>
      <div className="PRF-modal" onClick={(e) => e.stopPropagation()}>
        <div className="PRF-header">
          <h2 className="PRF-title">
            {postType === 'ride'
              ? t('renewal.create_similar_ride', { defaultValue: 'Create Similar Ride' })
              : t('renewal.create_similar_package', { defaultValue: 'Create Similar Package' })
            }
          </h2>
          <button className="PRF-close-button" onClick={onClose}>
            <FaTimes />
          </button>
        </div>
        
        <form className="PRF-form" onSubmit={handleSubmit}>
          {/* Monthly Limits Info */}
          {monthlyLimits && (
            <div className={`PRF-monthly-limits-info ${monthlyLimits.has_remaining ? '' : 'PRF-limit-reached'}`}>
              <div className="PRF-info-header">
                <FaInfoCircle />
                <span>Monthly {postType === 'ride' ? 'Ride' : 'Package'} Limit</span>
              </div>
              <div className="PRF-info-content">
                {monthlyLimits.max_allowed >= 9999 ? (
                  <span>You have unlimited {postType === 'ride' ? 'rides' : 'packages'} with your subscription</span>
                ) : (
                  <>
                    <span>
                      {monthlyLimits.remaining} / {monthlyLimits.max_allowed} {postType === 'ride' ? 'rides' : 'packages'} remaining
                    </span>
                    {monthlyLimits.next_reset_date && (
                      <span className="PRF-reset-date">Resets on {new Date(monthlyLimits.next_reset_date).toLocaleDateString()}</span>
                    )}
                  </>
                )}
                {!monthlyLimits.has_remaining && (
                  <div className="PRF-upgrade-action">
                    <span className="PRF-limit-reached-text">Monthly limit reached</span>
                    <CustomButton2 
                      label="Upgrade Plan" 
                      onClick={() => navigate('/subscription-plans')}
                    />
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Route Preview (Read-only) */}
          <div className="PRF-route-preview">
            <h3 className="PRF-section-title">
              <FaRoute /> {t('renewal.route_details', { defaultValue: 'Route Details' })}
            </h3>
            <div className="PRF-route-info">
              <div className="PRF-route-item">
                <FaMapMarkerAlt className="PRF-route-icon from" />
                <div>
                  <span className="PRF-route-label">{t('posts.from')}:</span>
                  <span className="PRF-route-value">{formatAddress('from')}</span>
                </div>
              </div>
              <div className="PRF-route-item">
                <FaMapMarkerAlt className="PRF-route-icon to" />
                <div>
                  <span className="PRF-route-label">{t('posts.to')}:</span>
                  <span className="PRF-route-value">{formatAddress('to')}</span>
                </div>
              </div>
              {originalPost.distance_km && (
                <div className="PRF-route-distance">
                  {t('posts.distance')}: {originalPost.distance_km} km
                </div>
              )}
            </div>
          </div>
          
          {/* Editable Fields */}
          <div className="PRF-editable-section">
            <h3 className="PRF-section-title">
              <FaCalendarAlt /> {t('renewal.travel_details', { defaultValue: 'Travel Details' })}
            </h3>
            
            {/* Advance Booking Limits Info */}
            {advanceBookingLimits && (
              <div className="PRF-advance-booking-info">
                <div className="PRF-info-header">
                  <FaInfoCircle />
                  <span>Advance Booking Limit</span>
                </div>
                <div className="PRF-info-content">
                  <span>{advanceBookingLimits.description}</span>
                  <span className="PRF-max-date">Maximum date: {new Date(advanceBookingLimits.max_date).toLocaleDateString()}</span>
                </div>
              </div>
            )}
            
            {/* Date Validation Error */}
            {dateValidationError && (
              <div className="PRF-date-validation-error">
                <FaExclamationTriangle />
                <div className="PRF-error-content">
                  <p>{dateValidationError.message}</p>
                  <p className="PRF-error-details">
                    Your {dateValidationError.userPlan} plan allows booking up to {dateValidationError.maxDays} days in advance.
                    You tried to book {dateValidationError.daysInAdvance} days in advance.
                  </p>
                  <CustomButton2 
                    label="Upgrade Plan" 
                    onClick={() => navigate('/subscription-plans')}
                  />
                </div>
              </div>
            )}
            
            {/* Date Selection */}
            <div className="PRF-form-group">
              <label className="PRF-label">
                {t('forms.travel_date')} <span className="required">*</span>
              </label>
              <UnifiedDatePicker
                selected={travelDate}
                onChange={handleDateChange}
                minDate={new Date()}
                maxDate={advanceBookingLimits ? new Date(advanceBookingLimits.max_date) : undefined}
                placeholderText={t('forms.select_date')}
                required
              />
            </div>
            
            {/* Time Selection */}
            <div className="PRF-form-group">
              <label className="PRF-label">
                {t('forms.travel_time')} <span className="required">*</span>
              </label>
              <UnifiedTimePicker
                selected={travelTime}
                onChange={handleTimeChange}
                placeholderText={t('forms.select_time')}
                required
              />
            </div>
            
            {/* Travel Type */}
            <div className="PRF-form-group">
              <label className="PRF-label">{t('forms.travel_type')}</label>
              <div className="PRF-radio-group">
                <RadioButton1
                  name="travelType"
                  label={t('posts.one_time')}
                  value="One-time"
                  checked={travelType === 'One-time'}
                  onChange={(e) => setTravelType(e.target.value)}
                />
                <RadioButton1
                  name="travelType"
                  label={t('posts.daily')}
                  value="Daily"
                  checked={travelType === 'Daily'}
                  onChange={(e) => setTravelType(e.target.value)}
                />
              </div>
            </div>
            
            {/* Description */}
            <div className="PRF-form-group">
              <label className="PRF-label">
                <FaFileAlt /> {t('forms.description')}
              </label>
              <textarea
                className="PRF-textarea"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={t('forms.description_placeholder', { 
                  defaultValue: 'Add any additional information...' 
                })}
                rows={4}
                maxLength={500}
              />
              <div className="PRF-char-count">
                {description.length}/500
              </div>
            </div>
          </div>
          
          {/* Action Buttons */}
          <div className="PRF-actions">
            <CustomButton10Co
              type="submit"
              disabled={isSubmitting || !!dateValidationError || limitsLoading || monthlyLimitsLoading || (monthlyLimits && !monthlyLimits.has_remaining)}
              style={{ minWidth: '150px' }}
            >
              {isSubmitting ? (
                <Spinner size="small" color="white" />
              ) : (
                t('buttons.create_post', { defaultValue: 'Create Post' })
              )}
            </CustomButton10Co>
            <CustomButton10Ca
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              label={t('buttons.cancel')}
              style={{ minWidth: '150px' }}
            />
          </div>
          
          {/* Show loading state */}
          {(limitsLoading || monthlyLimitsLoading) && (
            <div className="PRF-loading-info">
              <Spinner size="small" />
              <span>Loading limits...</span>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default PostRenewalForm;